import { create } from 'zustand'
import api from '@/lib/axios'

interface User {
  id: string
  firstname: string
  lastname: string
  email: string
  role: string
  establishmentId?: string
  avatar?: string
  fullname?: string
  userOnboarding?: boolean
  isPaid?: boolean
}

interface UserStore {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  sessionVerified: boolean
  
  // Actions
  setUser: (user: User) => void
  login: (user: User) => void
  logout: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  clearUser: () => void
  verifySession: (force?: boolean) => Promise<boolean>
  fetchUser: () => Promise<User | null>
  invalidateSession: () => void
  
  // Utility functions
  getUserFromStorage: () => User | null
  isUserLoggedIn: () => boolean
}

export const useUserStore = create<UserStore>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  sessionVerified: false,

  setUser: (user: User) => {
    set({ user, isAuthenticated: true, sessionVerified: true })
  },

  login: (user: User) => {
    set({ 
      user, 
      isAuthenticated: true, 
      isLoading: false,
      sessionVerified: true 
    })
    
    window.dispatchEvent(new Event('user-login'))
  },

  logout: async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Continue with local cleanup even if API fails
    }
    
    set({ 
      user: null, 
      isAuthenticated: false, 
      isLoading: false,
      sessionVerified: false 
    })
    
    window.dispatchEvent(new Event('user-logout'))
  },

  updateUser: (userData: Partial<User>) => {
    const currentUser = get().user
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData }
      set({ user: updatedUser })
    }
  },

  clearUser: () => {
    set({ 
      user: null, 
      isAuthenticated: false, 
      isLoading: false,
      sessionVerified: false 
    })
  },

  invalidateSession: () => {
    set({ sessionVerified: false })
  },

  fetchUser: async (): Promise<User | null> => {
    try {
      const response = await api.get('/user-controller/session')
      if (response.data) {
        const userData = response.data
        
        const establishmentId = 
          userData.establishmentId || 
          userData.etablissementId || 
          userData.etablissement?.id ||
          null
        
        const user: User = {
          id: userData.id,
          firstname: userData.firstname,
          lastname: userData.lastname,
          email: userData.email,
          role: userData.role,
          avatar: userData.avatar,
          fullname: userData.fullname,
          userOnboarding: userData.userOnboarding,
          isPaid: userData.isPaid,
          establishmentId: establishmentId,
        }
        set({ user, isAuthenticated: true, isLoading: false, sessionVerified: true })
        return user
      }
      return null
    } catch (error) {
      const currentUser = get().user
      if (!currentUser) {
        set({ user: null, isAuthenticated: false, isLoading: false, sessionVerified: true })
      }
      return null
    }
  },

  verifySession: async (force = false): Promise<boolean> => {
    // Skip API call if session was already verified (unless forced)
    if (!force && get().sessionVerified && get().user) {
      return true
    }

    try {
      const user = await get().fetchUser()
      if (user) {
        return true
      }
      const currentUser = get().user
      if (currentUser) {
        set({ isLoading: false, isAuthenticated: true, sessionVerified: true })
        return true
      }
      set({ user: null, isAuthenticated: false, isLoading: false, sessionVerified: true })
      return false
    } catch (error) {
      const currentUser = get().user
      if (currentUser) {
        set({ isLoading: false, isAuthenticated: true, sessionVerified: true })
        return true
      }
      set({ user: null, isAuthenticated: false, isLoading: false, sessionVerified: true })
      return false
    }
  },

  getUserFromStorage: () => {
    return get().user
  },

  isUserLoggedIn: () => {
    return !!get().user
  },
}))

// Initialize the store by fetching user from API (using HTTP-only cookie)
export const initializeUserStore = async () => {
  const state = useUserStore.getState()
  
  // A verified anonymous session is still verified. Re-checking it on every
  // public navigation made landing-page links wait on the API repeatedly.
  if (state.sessionVerified) {
    useUserStore.setState({ isLoading: false })
    return
  }
  
  // If user exists in store but session not yet verified, still skip
  // (they just logged in, session is fresh)
  if (state.user) {
    useUserStore.setState({ isLoading: false, isAuthenticated: true, sessionVerified: true })
    return
  }
  
  // No user in store - verify session (page refresh/reload scenario)
  await state.verifySession()
}

// Export types for use in other files
export type { User }
